
import Vue from "vue";
import { Component, Prop, Provide } from "vue-property-decorator";
import ControlsTree from "@/components/Reusable/ControlsTree/ControlsTree.vue";
import SearchBox, { SearchBoxState } from "./SearchBox.vue";
import { configStore, uiStore } from "@/store/store";
import DesignContentSettingsTabs from "./DesignContentSettingsTabs.vue";
import DesignTabContent from "./DesignTabContent.vue";
import { filterControlsThatCanBeModified } from "@/lib/permissions/utils";
import {
  filterBuilderElementControls,
  flattenAndCount
} from "@/lib/controls/search";
import Icon from "@/components/UI/Icon.vue";
import SearchBoxEmptyState from "@/components/ElementPropertiesPanel/SearchBoxEmptyState.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import ElementPresetChooser from "@/components/ElementPropertiesPanel/ElementPresetChooser.vue";
import { doesElementHavePreset } from "@/lib/presets/presets";
import { editingElementControlsTreeContext } from "@/lib/components/controls";

@Component({
  components: {
    ElementPresetChooser,
    DropdownInput,
    ControlsTree,
    SearchBox,
    DesignContentSettingsTabs,
    DesignTabContent,
    Icon,
    SearchBoxEmptyState
  }
})
export default class TabsAndControls extends Vue {
  @Prop()
  value!: boolean;

  searchBoxState: SearchBoxState = {
    modifiedOnly: false,
    includeSettings: false,
    query: ""
  };

  get element() {
    return uiStore.activeElement;
  }

  get elementId() {
    return uiStore.activeElementId;
  }

  clearQuery() {
    this.updateSearchBoxState({
      modifiedOnly: false,
      query: "",
      includeSettings: false
    });
  }

  updateSearchBoxState(newSearchBoxState: SearchBoxState) {
    this.searchBoxState = newSearchBoxState;
  }

  get hasSearchParameters() {
    return this.searchBoxState.modifiedOnly || this.searchBoxState.query;
  }

  get noSearchResults() {
    return (
      flattenAndCount(this.controlsFilteredBySearchParams.contentSections, 0) +
        flattenAndCount(this.controlsFilteredBySearchParams.designSections, 0) +
        flattenAndCount(
          this.controlsFilteredBySearchParams.settingsSections,
          0
        ) ===
      0
    );
  }

  get controlsFilteredBySearchParams() {
    const includeSettings = this.searchBoxState.includeSettings;
    const modifiedOnly = this.searchBoxState.modifiedOnly;
    const skipDesign = this.isPresetEnabled && !this.isPresetsOverridesEnabled;
    const query = this.searchBoxState.query;

    const controls = {
      designSections: skipDesign ? [] : this.controls.designSections,
      contentSections: this.controls.contentSections,
      settingsSections: includeSettings ? this.controls.settingsSections : []
    };

    const sourceControls = modifiedOnly ? this.controls : controls;

    return filterBuilderElementControls(
      sourceControls,
      modifiedOnly,
      query,
      this.editingElementControlsTreeContext
    );
  }

  @Provide("context")
  editingElementControlsTreeContext = editingElementControlsTreeContext;

  get controls() {
    // @ts-ignore
    const type = this.element.data.type;
    const controls = configStore.element(type).controls;

    // for client mode / permissions
    return filterControlsThatCanBeModified(controls, configStore.permission);
  }

  get isPresetEnabled() {
    if (!this.element) return false;
    return doesElementHavePreset(this.element.id);
  }

  get searchDisabled() {
    return (
      this.tab === "design" &&
      this.isPresetEnabled &&
      !this.isPresetsOverridesEnabled
    );
  }

  get isPresetsOverridesEnabled() {
    return configStore.isExperimentEnabled("design-presets-overrides");
  }

  get contentSections() {
    return this.controls.contentSections;
  }

  get designSections() {
    return this.controls.designSections;
  }

  get settingsSections() {
    return this.controls.settingsSections;
  }

  get hasContentControls() {
    return this.controls.contentSections.length > 0;
  }

  get hasDesignControls() {
    return this.controls.designSections.length > 0;
  }

  get hasSettingsControls() {
    return this.controls.settingsSections.length > 0;
  }

  get tab() {
    if (this.elementId && uiStore.activeElementProperties?.tab) {
      return uiStore.activeElementProperties.tab;
    }

    if (this.hasContentControls) return "content";
    if (this.hasDesignControls) return "design";
    if (this.hasSettingsControls) return "settings";

    return "";
  }

  updateTab(tab: "content" | "design" | "settings" | undefined) {
    if (!this.elementId) return;

    uiStore.setCurrentElementTab(tab);
  }

  searchString = "";
}
